'use client';

/**
 * useRealtimeStorySession — headless controller for a metered realtime
 * story-brief voice session. Extractable: only the start/settle route paths and
 * the StoryBrief tool name are portal-specific; the transport + accounting are
 * generic (webrtc.ts).
 *
 * Flow:
 *   start() → POST /api/realtime/story-session
 *     · 402            → error {insufficientTokens}   (UI falls back to type/from-text)
 *     · fake:true      → run a SCRIPTED mock (no OpenAI, demoable on dev)
 *     · else           → open WebRTC to OpenAI with the ephemeral value:
 *                        mic in, agent audio out, `oai-events` data channel.
 *   Live: render transcript deltas; countdown enforces maxSeconds client-side.
 *   End (brief received | cap hit | stop() | error):
 *     close the PC and POST /settle with the elapsed seconds.
 *
 * Exposes: { state, transcript, brief, secondsLeft, error, start, stop }.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { connectRealtime } from './webrtc';
import { connectGeminiLive, type RealtimeChannel } from './geminiLive';
import { connectRealtimeWS } from './realtimeWs';
import { attachLevelMeter } from '../audio/levelMeter';
import type {
  ExistingCharacter,
  NewCharacter,
  SessionError,
  SessionState,
  StartSessionResponse,
  StoryBrief,
  TranscriptEntry,
} from './types';

const START_ROUTE = '/api/realtime/story-session';
const OBSERVE_ROUTE = '/api/realtime/story-session/observe';
const REPORT_ROUTE = '/api/realtime/story-session/report';
const BRIEF_TOOL_NAME = 'submit_story_brief';
const SELECT_EXISTING_TOOL_NAME = 'select_existing_characters';
const ADD_NEW_TOOL_NAME = 'add_new_character';
const NOTE_TOOL_NAME = 'note_so_far';

/** The things the agent reports as settled, in the order the screen shows them. */
export const CAUGHT_FIELDS = ['heroes', 'place', 'what_happens', 'ending', 'kind'] as const;
export type CaughtField = (typeof CAUGHT_FIELDS)[number];

const norm = (s: string) => s.trim().toLowerCase();

interface UseRealtimeStorySessionOptions {
  locale: string;
  childName?: string;
  /** `together` = several children composing out loud (longer cap, own rules). */
  mode?: 'solo' | 'together';
}

export interface RealtimeStorySession {
  state: SessionState;
  transcript: TranscriptEntry[];
  brief: StoryBrief | null;
  secondsLeft: number;
  /** The cap the server granted this session (its own per-job value). */
  capSeconds: number;
  /** What the agent has reported as settled so far, for the on-screen cards. */
  caught: Partial<Record<CaughtField, string>>;
  error: SessionError | null;
  /** The family's existing characters, shown as pick-cards. */
  existingCharacters: ExistingCharacter[];
  /** Ids of existing characters chosen so far (by the agent or a card tap). */
  selectedCharacterIds: string[];
  /** New characters the child invented mid-session (via the agent's add tool). */
  newCharacters: NewCharacter[];
  /** User tapped a pick-card — toggle it + nudge the live agent to use/drop it. */
  pickCharacter: (id: string) => void;
  /** Live microphone level 0..1 while listening (0 otherwise). */
  micLevel: number;
  start: () => void;
  stop: () => void;
}

/**
 * How long a child may be silent before the interviewer decides and moves on.
 *
 * Long on purpose. A four-year-old takes seconds to start a sentence, and the
 * transcript of that sentence only arrives when they stop talking — a short
 * fuse made the agent talk over the answer it had just asked for, which is
 * worse than any silence.
 */
const NUDGE_AFTER_MS = 12000;
/** Mic level that counts as "somebody in the room is talking". */
const MIC_SPEAKING = 0.05;
/** Past this the child has left the room — the idle clock closes the call. */
const MAX_NUDGES = 2;

function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function coerceBrief(raw: unknown): StoryBrief | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const synopsis = typeof o.synopsis === 'string' ? o.synopsis.trim() : '';
  if (!synopsis) return null;
  const characters: Array<{ name: string; description?: string }> = [];
  if (Array.isArray(o.characters)) {
    for (const c of o.characters) {
      const co = (c ?? {}) as Record<string, unknown>;
      const name = typeof co.name === 'string' ? co.name.trim() : '';
      if (!name) continue;
      const description =
        typeof co.description === 'string' && co.description.trim()
          ? co.description.trim()
          : undefined;
      characters.push({ name, description });
    }
  }
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  return {
    synopsis,
    title: str(o.title),
    language: str(o.language),
    ageGroup: str(o.ageGroup),
    genre: str(o.genre),
    theme: str(o.theme),
    setting: str(o.setting),
    energy: num(o.energy),
    desiredDurationMinutes: num(o.desiredDurationMinutes),
    characters: characters.length ? characters : undefined,
    illustrationStyle: str(o.illustrationStyle),
    voicePreference: str(o.voicePreference),
  };
}

export function useRealtimeStorySession(
  opts: UseRealtimeStorySessionOptions,
): RealtimeStorySession {
  const { locale, childName, mode } = opts;

  const [state, setState] = useState<SessionState>('idle');
  const [micLevel, setMicLevel] = useState(0);
  /**
   * The interviewer keeps the conversation moving.
   *
   * A child goes quiet for all sorts of reasons — thinking, shy, distracted,
   * or the microphone missed them — and the agent sat in that silence until
   * the idle clock closed the call. An interviewer that waits is an
   * interviewer the child stops talking to. After a few seconds of nothing it
   * is nudged: decide something yourself, say it, ask the next question. Three
   * nudges per lull, then the existing idle close takes over.
   */
  const lastHeardRef = useRef(Date.now());
  const nudgesRef = useRef(0);
  /** True between response.created and response.done — never nudge into it. */
  const speakingRef = useRef(false);
  const stopMeterRef = useRef<(() => void) | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [brief, setBrief] = useState<StoryBrief | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [capSeconds, setCapSeconds] = useState(0);
  const [caught, setCaught] = useState<Partial<Record<CaughtField, string>>>({});
  const [error, setError] = useState<SessionError | null>(null);
  const [existingCharacters, setExistingCharacters] = useState<ExistingCharacter[]>([]);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([]);
  const [newCharacters, setNewCharacters] = useState<NewCharacter[]>([]);
  // Latest existing-character list, readable inside the stable event handler
  // (so name→id matching doesn't re-create handleEvent every render).
  const existingCharsRef = useRef<ExistingCharacter[]>([]);
  const selectedRef = useRef<string[]>([]);
  useEffect(() => {
    selectedRef.current = selectedCharacterIds;
  }, [selectedCharacterIds]);

  // ── Session-scoped mutable refs (never trigger renders) ──
  const connRef = useRef<RealtimeChannel | null>(null);
  const meteringRef = useRef<'worker' | 'client'>('worker'); // client = no sideband/relay → the browser reports its seconds
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fakeTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const startedAtRef = useRef<number>(0);
  const maxSecondsRef = useRef<number>(0);
  const reservationRef = useRef<string | null>(null);
  const settledRef = useRef(false);
  const endedRef = useRef(false);
  // Accumulated OpenAI usage across responses (from response.done.usage) — used
  // for COST discovery + reported to settle so we can price realtime by real
  // audio-token counts, not just wall-clock seconds.
  const usageRef = useRef({
    inputTokens: 0,
    outputTokens: 0,
    inputAudioTokens: 0,
    outputAudioTokens: 0,
    responses: 0,
  });

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    fakeTimeoutsRef.current.forEach((t) => clearTimeout(t));
    fakeTimeoutsRef.current = [];
  }, []);

  const elapsedSeconds = useCallback(() => {
    if (!startedAtRef.current) return 0;
    const raw = Math.ceil((Date.now() - startedAtRef.current) / 1000);
    return Math.max(0, Math.min(raw, maxSecondsRef.current || raw));
  }, []);

  // The client does NOT settle — the worker's trusted-usage sideband settles the
  // reservation authoritatively on call-close (client usage can't be trusted).
  // This only logs the client-side view for debugging.
  const settle = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    const seconds = elapsedSeconds();
    const usage = { ...usageRef.current };
    console.info('[realtime] client view (settle is server-authoritative)', { seconds, ...usage });
    // A websocket transport (Gemini Live) has no worker sideband: the client reports its
    // seconds (capped server-side by the reservation's maxSeconds) so the hold settles.
    if (meteringRef.current === 'client' && reservationRef.current) {
      const payload = JSON.stringify({ reservationId: reservationRef.current, seconds });
      try {
        if (!navigator.sendBeacon?.(REPORT_ROUTE, new Blob([payload], { type: 'application/json' }))) {
          void fetch(REPORT_ROUTE, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
        }
      } catch {
        /* best-effort */
      }
    }
  }, [elapsedSeconds]);

  /** Common teardown: stop timers, close transport, settle. */
  const teardown = useCallback(() => {
    clearTimers();
    stopMeterRef.current?.();
    stopMeterRef.current = null;
    if (connRef.current) {
      connRef.current.close();
      connRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        /* ignore */
      }
      audioRef.current.srcObject = null;
      audioRef.current = null;
    }
    settle();
  }, [clearTimers, settle]);

  const stop = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    teardown();
    setState((s) => (s === 'error' ? s : 'done'));
  }, [teardown]);

  const fail = useCallback(
    (code: SessionError['code'], message?: string) => {
      if (endedRef.current) {
        setError({ code, message });
        return;
      }
      endedRef.current = true;
      teardown();
      setError({ code, message });
      setState('error');
    },
    [teardown],
  );

  // ── Transcript accumulation ──
  const appendDelta = useCallback((role: TranscriptEntry['role'], delta: string) => {
    if (!delta) return;
    setTranscript((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === role && !last.final) {
        const next = prev.slice(0, -1);
        next.push({ ...last, text: last.text + delta });
        return next;
      }
      return [...prev, { id: uid(), role, text: delta, final: false }];
    });
  }, []);

  const finalizeTurn = useCallback((role: TranscriptEntry['role'], fullText?: string) => {
    setTranscript((prev) => {
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === role && !prev[i].final) {
          const next = prev.slice();
          next[i] = { ...next[i], final: true, text: fullText?.trim() || next[i].text };
          return next;
        }
      }
      if (fullText?.trim()) {
        return [...prev, { id: uid(), role, text: fullText.trim(), final: true }];
      }
      return prev;
    });
  }, []);

  const receiveBrief = useCallback(
    (raw: unknown) => {
      const parsed = coerceBrief(raw);
      if (parsed) {
        setBrief(parsed);
        stop();
      }
    },
    [stop],
  );

  // Resolve agent-spoken names to existing-character ids (name or synonym match).
  const selectExistingByNames = useCallback((names: unknown) => {
    if (!Array.isArray(names)) return;
    const wanted = names.filter((n): n is string => typeof n === 'string').map(norm);
    if (!wanted.length) return;
    /**
     * ONE character per name the agent said.
     *
     * A library grows duplicates — the same child saved as "Ника" three times
     * over a year — and matching by name selected all three: the cast list
     * came back as Ника, Ника and Ника, cast as the hero and two friends, and
     * the book would have paid for three references of one girl. The best
     * candidate is the one that has a picture, then the one that has been in
     * the most stories — and the list arrives newest-first, so equals are
     * resolved by recency without any further sorting.
     */
    const matched: string[] = [];
    for (const want of wanted) {
      const candidates = existingCharsRef.current.filter((c) =>
        [c.name, ...(c.synonyms ?? [])].map(norm).includes(want),
      );
      if (candidates.length === 0) continue;
      // The one with a picture wins; the list arrives newest-first, so the
      // first match is the most recent of equals.
      const best = candidates.find((c) => !!c.imageUrl) ?? candidates[0];
      matched.push(best.id);
    }
    if (matched.length) {
      setSelectedCharacterIds((prev) => Array.from(new Set([...prev, ...matched])));
    }
  }, []);

  const addNewCharacter = useCallback((raw: unknown) => {
    const o = (raw ?? {}) as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    if (!name) return;
    const description =
      typeof o.description === 'string' && o.description.trim() ? o.description.trim() : undefined;
    setNewCharacters((prev) =>
      prev.some((c) => norm(c.name) === norm(name)) ? prev : [...prev, { name, description }],
    );
  }, []);

  // One settled thing, straight onto the screen (note_so_far).
  const noteSoFar = useCallback((raw: unknown) => {
    const o = (raw ?? {}) as Record<string, unknown>;
    const field = typeof o.field === 'string' ? o.field : '';
    const value = typeof o.value === 'string' ? o.value.trim() : '';
    if (!value || !(CAUGHT_FIELDS as readonly string[]).includes(field)) return;
    setCaught((prev) => ({ ...prev, [field as CaughtField]: value }));
  }, []);

  // User tapped a pick-card: toggle it locally and, if a live call is up, drop a
  // user message into the conversation so the agent reflects the choice.
  const pickCharacter = useCallback((id: string) => {
    const char = existingCharsRef.current.find((c) => c.id === id);
    const has = selectedRef.current.includes(id);
    const next = has ? selectedRef.current.filter((x) => x !== id) : [...selectedRef.current, id];
    selectedRef.current = next;
    setSelectedCharacterIds(next);
    const conn = connRef.current;
    if (conn && char) {
      conn.send({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [
            {
              type: 'input_text',
              text: has
                ? `Actually, let's not use ${char.name}.`
                : `Let's include my character ${char.name} in the story.`,
            },
          ],
        },
      });
    }
  }, []);

  // Dispatch a completed tool call by name. `select_existing`/`add_new` update the
  // picker; `submit_story_brief` ends the session with the collected brief.
  const handleToolCall = useCallback(
    (name: string, argsJson: string) => {
      let args: unknown;
      try {
        args = JSON.parse(argsJson || '{}');
      } catch {
        return;
      }
      if (name === SELECT_EXISTING_TOOL_NAME) {
        selectExistingByNames((args as Record<string, unknown>).names);
      } else if (name === ADD_NEW_TOOL_NAME) {
        addNewCharacter(args);
      } else if (name === NOTE_TOOL_NAME) {
        noteSoFar(args);
      } else if (name === BRIEF_TOOL_NAME) {
        receiveBrief(args);
      }
    },
    [selectExistingByNames, addNewCharacter, noteSoFar, receiveBrief],
  );

  // ── OpenAI Realtime server-event handling ──
  /** Anything from either side means the conversation is alive. */
  const markAlive = useCallback(() => {
    lastHeardRef.current = Date.now();
    nudgesRef.current = 0;
  }, []);

  const handleEvent = useCallback(
    (event: Record<string, unknown>) => {
      const type = typeof event.type === 'string' ? event.type : '';
      // The child spoke, or the agent did: the lull is over either way.
      // ANY traffic means the conversation is alive; a response in flight
      // also closes the door on nudging.
      if (type) markAlive();
      if (type === 'response.created') speakingRef.current = true;
      if (type === 'response.done') speakingRef.current = false;
      switch (type) {
        case 'response.audio_transcript.delta':
        case 'response.output_audio_transcript.delta':
          appendDelta('agent', String(event.delta ?? ''));
          break;
        case 'response.audio_transcript.done':
        case 'response.output_audio_transcript.done':
          finalizeTurn('agent', typeof event.transcript === 'string' ? event.transcript : undefined);
          break;
        case 'conversation.item.input_audio_transcription.delta':
          appendDelta('user', String(event.delta ?? ''));
          break;
        case 'conversation.item.input_audio_transcription.completed':
          finalizeTurn('user', typeof event.transcript === 'string' ? event.transcript : undefined);
          break;
        case 'response.function_call_arguments.done': {
          if (typeof event.name === 'string') {
            handleToolCall(event.name, String(event.arguments ?? '{}'));
          }
          break;
        }
        case 'response.done': {
          // Accumulate usage for cost discovery + settle.
          const usage = (event.response as { usage?: Record<string, any> } | undefined)?.usage;
          if (usage) {
            const u = usageRef.current;
            u.responses += 1;
            u.inputTokens += Number(usage.input_tokens ?? 0);
            u.outputTokens += Number(usage.output_tokens ?? 0);
            u.inputAudioTokens += Number(usage.input_token_details?.audio_tokens ?? 0);
            u.outputAudioTokens += Number(usage.output_token_details?.audio_tokens ?? 0);
          }
          // Fallback: some stacks deliver the tool call inside the response.
          const resp = event.response as { output?: unknown } | undefined;
          const output = Array.isArray(resp?.output) ? resp!.output : [];
          for (const item of output) {
            const io = (item ?? {}) as Record<string, unknown>;
            if (io.type === 'function_call' && typeof io.name === 'string') {
              handleToolCall(io.name, String(io.arguments ?? '{}'));
            }
          }
          break;
        }
        case 'error':
          fail('generic', 'realtime session error');
          break;
        default:
          break;
      }
    },
    [appendDelta, finalizeTurn, handleToolCall, fail, markAlive],
  );

  /**
   * The nudge itself: a line the agent hears as coming from the room, not from
   * the child — "they are quiet, decide and go on". The instructions already
   * say deciding for them is the right move; this is what makes it happen
   * while the call is still open.
   */
  const nudge = useCallback(() => {
    const conn = connRef.current;
    if (!conn) return;
    const ru = locale === 'ru';
    const order = ru
      ? 'Ребёнок молчит. Не повторяй вопрос и не спрашивай, слышно ли тебя: выбери подходящий вариант сам, скажи его одной короткой весёлой фразой и сразу задай следующий вопрос. Эту служебную заметку вслух не читай.'
      : 'The child is quiet. Do not repeat the question and do not ask whether they can hear you: choose something sensible yourself, say it in one short cheerful line and ask the next question. Never read this note out loud.';
    /**
     * `response.create` carries the order, so nothing is added to the
     * conversation: the documented way to steer ONE response on the Realtime
     * API is its own `instructions`, and a fake user turn would otherwise sit
     * in the context being referred back to ("you said you were quiet").
     * Gemini Live has no per-response instructions — its channel turns this
     * into the client text turn its API documents instead (geminiLive.ts).
     */
    conn.send({ type: 'response.create', response: { instructions: order } });
    lastHeardRef.current = Date.now();
    nudgesRef.current += 1;
  }, [locale]);

  /**
   * Watch the lull. Three nudges is where it stops: past that the child has
   * left the room, and the idle clock should close the call rather than have
   * an agent narrating a story to nobody.
   */
  useEffect(() => {
    if (state !== 'listening') return;
    const id = setInterval(() => {
      if (Date.now() - lastHeardRef.current < NUDGE_AFTER_MS) return;
      if (nudgesRef.current >= MAX_NUDGES) return;
      if (speakingRef.current) return;
      nudge();
    }, 1500);
    return () => clearInterval(id);
  }, [state, nudge]);

  // ── Countdown → auto wrap-up at the cap ──
  const startCountdown = useCallback(() => {
    setSecondsLeft(maxSecondsRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          // Cap reached: end + settle. Any brief already captured is kept.
          stop();
          return 0;
        }
        return next;
      });
    }, 1000);
  }, [stop]);

  // ── Fake scripted session (dev, no OpenAI) ──
  const runFake = useCallback(() => {
    setState('listening');
    startedAtRef.current = Date.now();
    startCountdown();
    const push = (role: TranscriptEntry['role'], text: string, at: number) => {
      fakeTimeoutsRef.current.push(
        setTimeout(() => {
          setTranscript((prev) => [...prev, { id: uid(), role, text, final: true }]);
        }, at),
      );
    };
    push('agent', 'Hi! I love making up stories. Who is the hero of your story?', 300);
    push('user', 'A little dragon named Ember who is afraid of the dark.', 1600);
    // Fake the agent registering Ember as a NEW invented character (drives the
    // save-to-library + portrait wiring on the dev fake stand).
    fakeTimeoutsRef.current.push(
      setTimeout(() => {
        handleToolCall(
          ADD_NEW_TOOL_NAME,
          JSON.stringify({ name: 'Ember', description: 'a small purple dragon afraid of the dark' }),
        );
      }, 2000),
    );
    push('agent', 'A brave little dragon — wonderful! What happens to Ember?', 2600);
    push('user', 'She finds a glowing friend and learns the dark can be cosy.', 3800);
    push('agent', 'That is lovely. Let me put your story together now.', 4600);
    fakeTimeoutsRef.current.push(
      setTimeout(() => {
        receiveBrief({
          synopsis:
            'Ember, a little dragon afraid of the dark, befriends a glowing creature and discovers that the night can be warm and cosy.',
          title: 'Ember and the Night Light',
          language: locale,
          genre: 'adventure',
          theme: 'courage',
          setting: 'fairytale',
          energy: 3,
          desiredDurationMinutes: 5,
          characters: [{ name: 'Ember', description: 'a small dragon afraid of the dark' }],
        });
      }, 5200),
    );
  }, [locale, receiveBrief, startCountdown, handleToolCall]);

  // ── Real WebRTC session ──
  const runReal = useCallback(
    async (session: StartSessionResponse) => {
      setState('connecting');

      let micStream: MediaStream;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        fail('micDenied');
        return;
      }
      if (endedRef.current) {
        micStream.getTracks().forEach((t) => t.stop());
        return;
      }

      const audioEl = new Audio();
      audioEl.autoplay = true;
      audioRef.current = audioEl;
      meteringRef.current = session.metering ?? (session.transport === 'websocket' ? 'client' : 'worker');

      try {
        const conn: RealtimeChannel = session.transport === 'websocket' && session.provider === 'google_ai_studio'
          ? await connectGeminiLive({
              // Gemini Live: WebSocket, PCM in/out in the transport; the persona + the
              // submit_story_brief tool ride in the browser's setup message.
              ephemeralKey: session.value,
              wsUrl: session.wsUrl ?? undefined,
              wsAuthParam: session.wsAuthParam,
              micStream,
              session: {
                instructions: session.sessionConfig?.instructions ?? '',
                tools: session.sessionConfig?.tools ?? [],
                voice: session.voice ?? 'Leda',
                model: session.model,
                greeting: locale === 'ru' ? 'Поздоровайся и начни интервью: спроси, кто герой истории.' : 'Say hello and start the interview: ask who the hero of the story is.',
              },
              onEvent: handleEvent,
              onError: (err) => fail('generic', err.message),
            })
          : session.transport === 'websocket' && session.wsUrl
          ? await connectRealtimeWS({
              // xAI Grok Voice: WebSocket, OpenAI-shaped events; through the worker relay in production
              ephemeralKey: session.value,
              wsUrl: session.wsUrl,
              wsAuthParam: session.wsAuthParam === 'ticket' ? 'ticket' : 'subprotocol',
              micStream,
              session: {
                instructions: session.sessionConfig?.instructions ?? '',
                tools: session.sessionConfig?.tools ?? [],
                voice: session.voice ?? 'eve',
                model: session.model,
                language: locale,
                greeting: locale === 'ru' ? 'Поздоровайся и начни интервью: спроси, кто герой истории.' : 'Say hello and start the interview: ask who the hero of the story is.',
              },
              onEvent: handleEvent,
              onError: (err) => fail('generic', err.message),
            })
          : await connectRealtime({
          ephemeralKey: session.value,
          model: session.model,
          callsUrl: session.webrtcUrl ?? undefined,
          micStream,
          audioEl,
          onEvent: handleEvent,
          onError: (err) => fail('generic', err.message),
          onOpen: () => {
            // Kick the interviewer to greet first (worker already stamped the
            // instructions + submit_story_brief tool into the session).
            conn.send({ type: 'response.create' });
          },
        });
        if (endedRef.current) {
          conn.close();
          return;
        }
        connRef.current = conn;
        startedAtRef.current = Date.now();
        // Start the server-side trusted-usage sideband (which will settle on
        // call-close). Best-effort: without it the reservation just TTL-expires.
        if (conn.callId && reservationRef.current) {
          fetch(OBSERVE_ROUTE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reservationId: reservationRef.current, callId: conn.callId }),
          }).catch(() => {});
        }
        // Live mic level for the UI (same meter as every other recorder).
        stopMeterRef.current?.();
        stopMeterRef.current = attachLevelMeter(micStream, (level) => {
          setMicLevel(level);
          // The child is making a sound RIGHT NOW: whatever they are saying,
          // the agent must not start over the top of it. The transcript of a
          // slow sentence arrives only when the turn ends, which is why the
          // nudge cannot wait for events alone.
          if (level > MIC_SPEAKING) lastHeardRef.current = Date.now();
        });
        setState('listening');
        startCountdown();
      } catch (err) {
        micStream.getTracks().forEach((t) => t.stop());
        fail('generic', err instanceof Error ? err.message : String(err));
      }
    },
    [fail, handleEvent, startCountdown, locale],
  );

  const start = useCallback(() => {
    if (state !== 'idle' && state !== 'error' && state !== 'done') return;
    // Reset session state.
    endedRef.current = false;
    settledRef.current = false;
    reservationRef.current = null;
    startedAtRef.current = 0;
    setTranscript([]);
    setBrief(null);
    setError(null);
    setSecondsLeft(0);
    setSelectedCharacterIds([]);
    setNewCharacters([]);
    selectedRef.current = [];
    setState('starting');

    (async () => {
      let session: StartSessionResponse;
      try {
        const resp = await fetch(START_ROUTE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locale, childName, mode }),
        });
        if (resp.status === 402) {
          setError({ code: 'insufficientTokens' });
          setState('error');
          return;
        }
        if (!resp.ok) {
          setError({ code: 'generic' });
          setState('error');
          return;
        }
        session = (await resp.json()) as StartSessionResponse;
      } catch {
        setError({ code: 'generic' });
        setState('error');
        return;
      }

      reservationRef.current = session.reservationId;
      maxSecondsRef.current = session.maxSeconds || 120;
      setCapSeconds(maxSecondsRef.current);
      const chars = session.existingCharacters ?? [];
      existingCharsRef.current = chars;
      setExistingCharacters(chars);

      if (session.fake) {
        runFake();
      } else {
        await runReal(session);
      }
    })();
  }, [state, locale, childName, mode, runFake, runReal]);

  // Clean up on unmount (settle any live reservation).
  useEffect(() => {
    return () => {
      if (!endedRef.current) {
        endedRef.current = true;
        teardown();
      }
    };
    // teardown is stable enough; we only want unmount behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    transcript,
    brief,
    secondsLeft,
    capSeconds,
    caught,
    error,
    existingCharacters,
    selectedCharacterIds,
    newCharacters,
    pickCharacter,
    micLevel,
    start,
    stop,
  };
}
